/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
  line-height: 0;
}

.qds-radio,
.qds-box,
.qds-icon {
  grid-area: radio;
  place-self: center;
}

.qds-icon,
.qds-box {
  display: none;
  pointer-events: none;
}

.qds-box {
  border-radius: var(--qds-control-rounded-border-radius);
}

.qds-container {
  display: grid;
  align-self: flex-start;
  grid-template-areas: 'radio';
}

.qds-icon {
  color: var(--qds-theme-signature-color-contrast);
}

.qds-label {
  cursor: pointer;
  display: inline-flex;
  width: inherit;
}

.qds-label-inner {
  flex: 1 1 auto;
  min-width: 0;
}

.qds-radio {
  appearance: none;
  background-color: var(--qds-theme-control-input-background);
  border-radius: var(--qds-control-rounded-border-radius);
  border: var(--qds-control-border-width) solid var(--qds-theme-control-border);
  cursor: pointer;
  margin: 0;
  print-color-adjust: exact;

  &:focus-visible {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    outline-offset: var(--qds-focus-border-offset);
  }

  &:hover ~ .qds-box,
  &:active ~ .qds-box {
    display: block;
  }

  &:hover ~ .qds-box {
    background-color: var(--qds-theme-signature-color-hover);
  }

  &:active ~ .qds-box {
    background-color: var(--qds-theme-signature-color-pressed);
  }

  &:checked {
    background-color: var(--qds-theme-signature-color-default);
    border-color: transparent;

    & ~ .qds-icon {
      display: block;
    }

    &:hover {
      background-color: var(--qds-theme-signature-color-hover);
    }

    &:active {
      background-color: var(--qds-theme-signature-color-pressed);
    }
  }
}

[aria-disabled='true'] {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

[data-size='small'] {
  &.qds-radio {
    width: var(--qds-control-toggle-box-small-size);
    height: var(--qds-control-toggle-box-small-size);
  }

  &.qds-icon {
    font-size: var(--qds-control-small-icon-size);
  }

  &.qds-box {
    width: var(--qds-control-toggle-indicator-small-size);
    height: var(--qds-control-toggle-indicator-small-size);
  }

  &.qds-label {
    gap: var(--qds-control-small-gap-siblings-related);
    min-height: var(--qds-control-toggle-small-height);
  }

  &.qds-inline {
    margin-block: var(--qds-control-small-padding-auto-height);
    min-height: var(--qds-control-small-height);
  }
}

[data-size='standard'] {
  &.qds-radio {
    width: var(--qds-control-toggle-box-standard-size);
    height: var(--qds-control-toggle-box-standard-size);
  }

  &.qds-icon {
    font-size: var(--qds-control-standard-icon-size);
  }

  &.qds-box {
    width: var(--qds-control-toggle-indicator-standard-size);
    height: var(--qds-control-toggle-indicator-standard-size);
  }

  &.qds-label {
    gap: var(--qds-control-standard-gap-siblings-related);
    min-height: var(--qds-control-toggle-standard-height);
  }

  &.qds-inline {
    margin-block: var(--qds-control-standard-padding-auto-height);
    min-height: var(--qds-control-standard-height);
  }
}

[data-size='large'] {
  &.qds-radio {
    width: var(--qds-control-toggle-box-large-size);
    height: var(--qds-control-toggle-box-large-size);
  }

  &.qds-icon {
    font-size: var(--qds-control-large-icon-size);
  }

  &.qds-box {
    width: var(--qds-control-toggle-indicator-large-size);
    height: var(--qds-control-toggle-indicator-large-size);
  }

  &.qds-label {
    gap: var(--qds-control-large-gap-siblings-related);
    min-height: var(--qds-control-toggle-large-height);
  }

  &.qds-inline {
    margin-block: var(--qds-control-large-padding-auto-height);
    min-height: var(--qds-control-large-height);
  }
}
